import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { FieldWithStatusIndicator } from '@/components/common/item-field-value/FieldWithStatusIndicator';
import { useItemsBulkFetchQueryWithPublished } from '@/components/modular-item-panel/hooks/useItemsBulkFetchQueryWithPublished';
import type { ItemMultiItemValue } from '@/lib/api/types/items';
import type { ItemTypeMap } from '@/lib/api/types/itemType';
import { associateById } from '@/lib/utils/common/associateById';
import { ItemInlineChip } from './ItemInlineChip';
import { ItemSelectionDialog } from './ItemSelectionDialog';
import { MultiValueFieldManager, type MultiValueFieldValue } from './MultiValueFieldManager';
import { MULTI_VALUE_MAX_COUNT } from './validation';

interface MultiItemFieldProps {
  id: string;
  name: string;
  values?: ItemMultiItemValue[];
  itemTypesById: ItemTypeMap;
  isEditable?: boolean;
  autoFocus?: boolean;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
  onChange: (selectedIds: number[]) => void;
  onValidationChange?: (error: string | null) => void;
}

export const MultiItemField = ({
  name,
  values = [],
  itemTypesById,
  isEditable = false,
  autoFocus = false,
  isPending = false,
  isSuccess = false,
  isError,
  error,
  onChange,
  onValidationChange,
}: MultiItemFieldProps) => {
  const { t } = useTranslation();
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(autoFocus && values.length === 0);

  const sortedValues = React.useMemo(() => {
    return [...values].sort((a, b) => {
      if (a.id < 0) return 1;
      if (b.id < 0) return -1;
      return a.id - b.id;
    });
  }, [values]);

  const itemIds = React.useMemo(() => sortedValues.map((v) => v.MULTI_ITEM), [sortedValues]);
  const { data: items = [] } = useItemsBulkFetchQueryWithPublished({ itemIds });
  const itemsById = associateById(items);

  const handleValidationError = (err: string | null) => {
    setValidationError(err);
    onValidationChange?.(err);
  };

  const handleOnAdd = (itemId: number) => {
    if (itemIds.includes(itemId)) return;
    if (values.length >= MULTI_VALUE_MAX_COUNT) {
      handleValidationError(
        t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiValueMaxCountReached, {
          maxCount: MULTI_VALUE_MAX_COUNT,
        }),
      );
      return;
    }
    handleValidationError(null);
    onChange([...itemIds, itemId]);
  };

  const fieldValues: MultiValueFieldValue<ItemMultiItemValue>[] = values.map((v) => ({
    id: v.id,
    value: v,
  }));

  const resolvedIsError = !!validationError || !!isError;
  const resolvedError = validationError ? new Error(validationError) : (error ?? null);

  return (
    <FieldWithStatusIndicator name={name} isPending={isPending} isSuccess={isSuccess}>
      <div className="flex flex-col gap-1">
        <MultiValueFieldManager
          values={fieldValues}
          isEditable={isEditable}
          disabled={isPending}
          onRemove={(removedId) =>
            onChange(values.filter((v) => v.id !== removedId).map((v) => v.MULTI_ITEM))
          }
          maxCount={MULTI_VALUE_MAX_COUNT}
          onAddClick={() => setDialogOpen(true)}
        >
          {(item) => {
            const itemData = itemsById[item.value.MULTI_ITEM];
            return itemData ? (
              <ItemInlineChip
                name={itemData.name}
                itemTypeId={itemData.itemTypeId}
                itemTypesById={itemTypesById}
              />
            ) : null;
          }}
        </MultiValueFieldManager>
        {resolvedIsError && resolvedError && (
          <span
            className="text-xs text-text-error"
            role="alert"
            data-testid="multi-item-field-error"
          >
            {resolvedError.message}
          </span>
        )}
      </div>

      <ItemSelectionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        itemTypesById={itemTypesById}
        excludedItemIds={itemIds}
        onSave={handleOnAdd}
        title={t(($) => $.adaptivePlanner.itemPanel.customFields.multiValue.addValue)}
      />
    </FieldWithStatusIndicator>
  );
};
